import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AvailableServices, MeetingViewModelService } from '@vendasta/integrations';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { getMeetingIntegrationIconUrl } from '../../utils';

interface IntegrationContext {
  service: AvailableServices;
  iconUrl: string;
  label: string;
  connected: boolean;
}

export interface IntegrationServiceSelection {
  service: AvailableServices;
  isConnected: boolean;
}

@Component({
  selector: 'meeting-scheduler-integration-selection',
  templateUrl: './integration-selection.component.html',
  styleUrls: ['./integration-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IntegrationSelectionComponent implements OnInit, OnDestroy {
  _initialSelection: AvailableServices;
  googleService: AvailableServices = AvailableServices.GOOGLE_MEET;

  @Input() set initialSelection(val: AvailableServices | undefined) {
    if (val && this.integrationFormControl && this.integrationFormControl.pristine) {
      this.integrationFormControl.setValue(val);
    }
    this._initialSelection = val;
  }

  @Output() integrationSelected: EventEmitter<IntegrationServiceSelection> = new EventEmitter();
  form: UntypedFormGroup;
  integrationFormControl: UntypedFormControl;
  googleMeetContext$: Observable<IntegrationContext>;
  zoomContext$: Observable<IntegrationContext>;
  microsoftContext$: Observable<IntegrationContext>;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly cd: ChangeDetectorRef,
    private readonly meetingViewModelService: MeetingViewModelService,
  ) {}

  ngOnInit(): void {
    this.integrationFormControl = this.fb.control(this._initialSelection, [Validators.required, Validators.min(1)]);
    this.form = this.fb.group({
      integration: this.integrationFormControl,
    });

    // These have to be observables because we only want to show the connected / disconnected portion depending on if
    // the service is connected. Otherwise, we have 2 radio buttons for each service and they don't show correctly.
    this.googleMeetContext$ = this.meetingViewModelService.isServiceConnected$(AvailableServices.GOOGLE_MEET).pipe(
      map((connected) => {
        return {
          label: 'Google Meet',
          service: AvailableServices.GOOGLE_MEET,
          iconUrl: getMeetingIntegrationIconUrl('meet-icon'),
          connected,
        };
      }),
    );
    this.zoomContext$ = this.meetingViewModelService.isServiceConnected$(AvailableServices.ZOOM).pipe(
      map((connected) => {
        return {
          label: 'Zoom',
          service: AvailableServices.ZOOM,
          iconUrl: getMeetingIntegrationIconUrl('zoom-icon'),
          connected,
        };
      }),
    );

    this.microsoftContext$ = this.meetingViewModelService.isServiceConnected$(AvailableServices.MICROSOFT).pipe(
      map((connected) => ({
        label: 'Microsoft Teams',
        service: AvailableServices.MICROSOFT,
        iconUrl: getMeetingIntegrationIconUrl('teams-icon'),
        connected,
      })),
    );

    this.subscriptions.push(
      this.integrationFormControl.valueChanges
        .pipe(
          switchMap((service) => {
            return combineLatest([
              of(service),
              this.meetingViewModelService.isServiceConnected$(service).pipe(distinctUntilChanged()),
            ]);
          }),
          map(([service, isConnected]) => ({ service, isConnected })),
        )
        .subscribe((selection) => {
          this.cd.markForCheck();
          this.integrationSelected.emit(selection);
        }),
    );

    this.cd.markForCheck();
  }

  setSelected(service: AvailableServices): void {
    this.integrationFormControl.setValue(service);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
